import { useEffect, useRef } from 'react';
import { default as LazyRender, LazyLoadProps } from 'react-lazyload';

export interface OnScrollToCallbackFns {
  onScrollToBottom?: VoidFunction;
  onScrollFromBottom?: VoidFunction;
}

// Leverage LazyRender and component mount/unmount to trigger onScrollX callbacks

const OnScrollToCallbacksInner = ({
  onScrollToBottom,
  onScrollFromBottom,
}: OnScrollToCallbackFns) => {
  useEffect(() => {
    onScrollToBottom?.();
    return onScrollFromBottom;
    // Intentionally only run on mount/unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

// Pixel threshold for considering the user "at the bottom" of a scroll container.
// Also accounts for Safari's elastic "rubber-band" scrolling at boundaries, where
// the scroll position momentarily extends beyond content bounds during the bounce.
const SCROLL_BOTTOM_THRESHOLD = 50;

/**
 * Direct scroll-to-bottom detection for explicit scroll containers.
 * Uses only vertical scroll position, avoiding react-lazyload's visibility
 * calculation which is incorrectly affected by horizontal scrolling.
 */
const ScrollContainerBottom = ({
  scrollContainer,
  onScrollToBottom,
  onScrollFromBottom,
}: OnScrollToCallbackFns & { scrollContainer: HTMLElement }) => {
  const isAtBottomRef = useRef(false);

  useEffect(() => {
    const check = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const nearBottom =
        scrollHeight - clientHeight - scrollTop < SCROLL_BOTTOM_THRESHOLD;
      if (nearBottom && !isAtBottomRef.current) {
        isAtBottomRef.current = true;
        onScrollToBottom?.();
      } else if (!nearBottom && isAtBottomRef.current) {
        isAtBottomRef.current = false;
        onScrollFromBottom?.();
      }
    };

    scrollContainer.addEventListener('scroll', check);
    check();
    return () => scrollContainer.removeEventListener('scroll', check);
  }, [scrollContainer, onScrollToBottom, onScrollFromBottom]);

  return null;
};

export const OnScrollToCallbacks = ({
  onScrollFromBottom,
  onScrollToBottom,
  scrollContainer,
  ...lazyLoadProps
}: OnScrollToCallbackFns & LazyLoadProps) => {
  if (!onScrollFromBottom) {
    return null;
  }

  // When a scroll container element is provided, use direct vertical scroll
  // detection instead of react-lazyload. react-lazyload's visibility check
  // is affected by horizontal scroll position in overflow containers, which
  // causes false "scroll from bottom" events during horizontal scrolling.
  if (scrollContainer instanceof HTMLElement) {
    return (
      <ScrollContainerBottom
        scrollContainer={scrollContainer}
        onScrollToBottom={onScrollToBottom}
        onScrollFromBottom={onScrollFromBottom}
      />
    );
  }

  return (
    <LazyRender
      key="onScrollToCallbacks"
      unmountIfInvisible
      overflow
      scroll
      resize
      height={1}
      // Offset allows the element to be considered "visible" before it actually
      // enters the viewport, and keeps it visible during Safari's elastic scroll bounce
      offset={SCROLL_BOTTOM_THRESHOLD}
      scrollContainer={scrollContainer}
      {...lazyLoadProps}
    >
      <OnScrollToCallbacksInner
        onScrollFromBottom={onScrollFromBottom}
        onScrollToBottom={onScrollToBottom}
      />
    </LazyRender>
  );
};
